/**
 * The Numtable library: built-in, read-only reference documents every
 * signed-in user can open — public data (holidays, exchange rates), not tenant
 * data, so they are not rows in `folders_nodes` and belong to no organization.
 * The Worker assembles and caches each one; see worker/routes/library*.ts.
 */

export const LIBRARY_DOCUMENTS = ['holidays', 'exchange-rates'] as const;
export type LibraryDocumentId = (typeof LIBRARY_DOCUMENTS)[number];

export const HOLIDAY_COUNTRIES = ['AR', 'BR', 'CL', 'CO', 'ES', 'MX', 'PE', 'US', 'UY'] as const;
export type HolidayCountry = (typeof HOLIDAY_COUNTRIES)[number];

export interface LibraryHoliday {
  readonly date: string;
  readonly name: string;
  readonly type: string;
  readonly notes: string;
}

export type HolidayCountryResult =
  | { readonly days: readonly LibraryHoliday[]; readonly provider: string; readonly fetchedAt: string; readonly stale: boolean }
  | { readonly error: string };

export interface LibraryHolidays {
  readonly year: number;
  readonly countries: Partial<Record<HolidayCountry, HolidayCountryResult>>;
}

export interface RateQuote {
  readonly buy: number | null;
  readonly sell: number | null;
  readonly avg: number | null;
}

export interface ExchangeRateSheet {
  readonly currency: string;
  readonly columns: readonly string[];
  readonly notes?: string;
  readonly rows: readonly { readonly date: string; readonly values: Readonly<Record<string, RateQuote>> }[];
}

export interface LibraryExchangeRates {
  readonly updatedAt: string;
  readonly errors: readonly string[];
  readonly sheets: readonly ExchangeRateSheet[];
}

async function getJson<T>(url: string, accessToken: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` }, ...(signal ? { signal } : {}) });
  if (!response.ok) throw new Error(`library_${response.status}`);
  return (await response.json()) as T;
}

export function loadLibraryHolidays(year: number, accessToken: string, signal?: AbortSignal) {
  return getJson<LibraryHolidays>(`/api/library/holidays/${year}`, accessToken, signal);
}

export function loadLibraryExchangeRates(accessToken: string, signal?: AbortSignal) {
  return getJson<LibraryExchangeRates>('/api/library/exchange-rates', accessToken, signal);
}
